import {
  BOM_VALIDATIONS_ROUTE,
  INSPECTOR_ROUTE,
  ON_DEMAND_EXPORTS_ROUTE,
  PAC_SAVED_LISTS_ROUTE,
  SAVED_TABLES_ROUTE,
  SCHEDULES_ROUTE,
  TEST_CASES_ROUTE
} from '@/router/routeNames'

export const ACTIVITY_CENTER_TAB_NAMES = Object.freeze({
  ALL: 'all',
  FAILED: 'failed',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed'
})

export const ACTIVITY_CENTER_TABS = Object.freeze([
  { name: ACTIVITY_CENTER_TAB_NAMES.ALL, label: 'All', dot: null, count: 0 },
  { name: ACTIVITY_CENTER_TAB_NAMES.FAILED, label: 'Failed', dot: '#E5006D', count: 0 },
  { name: ACTIVITY_CENTER_TAB_NAMES.IN_PROGRESS, label: 'In Progress', dot: '#1E6FA8', count: 0 },
  { name: ACTIVITY_CENTER_TAB_NAMES.COMPLETED, label: 'Completed', dot: '#B8D935', count: 0 }
])

export const ACTIVITY_CENTER_FEATURE_TAB_NAMES = Object.freeze({
  ALL: 'all',
  BOM_VALIDATIONS: 'bom-validations',
  TEST_CASE_VALIDATIONS: 'test-case-validations',
  EXPORTS: 'exports',
  SAVED_TABLES: 'saved-tables',
  INSPECTIONS: 'inspections',
  ON_DEMAND_EXPORTS: 'on-demand-exports',
  AMBIGUOUS_COMBINATIONS: 'ambiguous-combinations',
  SCHEDULES: 'schedules'
})

export const ACTIVITY_CENTER_FEATURE_TABS = Object.freeze([
  { name: ACTIVITY_CENTER_FEATURE_TAB_NAMES.ALL, label: 'All', count: 0 },
  { name: ACTIVITY_CENTER_FEATURE_TAB_NAMES.BOM_VALIDATIONS, label: 'BOM Validations', count: 0 },
  { name: ACTIVITY_CENTER_FEATURE_TAB_NAMES.TEST_CASE_VALIDATIONS, label: 'Test Case Validations', count: 0 },
  { name: ACTIVITY_CENTER_FEATURE_TAB_NAMES.EXPORTS, label: 'Exports', count: 0 },
  { name: ACTIVITY_CENTER_FEATURE_TAB_NAMES.SAVED_TABLES, label: 'Saved Tables', count: 0 },
  { name: ACTIVITY_CENTER_FEATURE_TAB_NAMES.INSPECTIONS, label: 'Inspections', count: 0 },
  { name: ACTIVITY_CENTER_FEATURE_TAB_NAMES.ON_DEMAND_EXPORTS, label: 'On Demand Exports', count: 0 },
  { name: ACTIVITY_CENTER_FEATURE_TAB_NAMES.AMBIGUOUS_COMBINATIONS, label: 'Ambiguous Combinations', count: 0 },
  { name: ACTIVITY_CENTER_FEATURE_TAB_NAMES.SCHEDULES, label: 'Schedules', count: 0 }
])

export const ACTIVITY_CENTER_JOB_TYPE_ROUTE_NAMES = Object.freeze({
  USER_EXPORT: ON_DEMAND_EXPORTS_ROUTE.name,
  COMPONENT_VALIDATION: BOM_VALIDATIONS_ROUTE.name,
  TEST_CASE_VALIDATION: TEST_CASES_ROUTE.name,
  UNUSABLE_CHOICES_VALIDATION: TEST_CASES_ROUTE.name,
  INSPECTION: INSPECTOR_ROUTE.name,
  CONFIGURATION_LIST_EXPORT: PAC_SAVED_LISTS_ROUTE.name,
  CALCULATE_COMBINATIONS: SAVED_TABLES_ROUTE.name,
  AMBIGUOUS_COMBINATION: SAVED_TABLES_ROUTE.name,
  SCHEDULED_COLLECTION: SCHEDULES_ROUTE.name
})

export const getActivityCenterDestination = activity => {
  const jobType = activity?.data?.jobType?.toUpperCase()
  // eslint-disable-next-line security/detect-object-injection
  const routeName = ACTIVITY_CENTER_JOB_TYPE_ROUTE_NAMES[jobType]

  return routeName ? { name: routeName } : null
}

export const ACTIVITY_CENTER_TIME_PERIOD_NAMES = Object.freeze({
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_7_DAYS: 'last-7-days',
  LAST_30_DAYS: 'last-30-days'
})

export const ACTIVITY_CENTER_TIME_PERIODS = Object.freeze([
  { name: ACTIVITY_CENTER_TIME_PERIOD_NAMES.TODAY, label: 'Today' },
  { name: ACTIVITY_CENTER_TIME_PERIOD_NAMES.YESTERDAY, label: 'Yesterday' },
  { name: ACTIVITY_CENTER_TIME_PERIOD_NAMES.LAST_7_DAYS, label: 'Last 7 days' },
  { name: ACTIVITY_CENTER_TIME_PERIOD_NAMES.LAST_30_DAYS, label: 'Last 30 days' }
])

export const ACTIVITY_CENTER_STATUS_GROUPS = Object.freeze([
  {
    name: ACTIVITY_CENTER_TAB_NAMES.FAILED,
    label: 'Failed',
    color: '#dc2626',
    backgroundColor: '#fde3e8'
  },
  {
    name: ACTIVITY_CENTER_TAB_NAMES.IN_PROGRESS,
    label: 'In Progress',
    color: '#4b96d2',
    backgroundColor: '#e5f2f9'
  },
  {
    name: ACTIVITY_CENTER_TAB_NAMES.COMPLETED,
    label: 'Completed',
    color: '#2d6300',
    backgroundColor: '#f3f7e4'
  }
])

export const ACTIVITY_CENTER_MOCK_ACTIVITIES = Object.freeze([
  {
    id: 'bom-validation-failed-1',
    feature: ACTIVITY_CENTER_FEATURE_TAB_NAMES.BOM_VALIDATIONS,
    status: ACTIVITY_CENTER_TAB_NAMES.FAILED,
    title: 'Feature name',
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60000).toISOString(),
    message: 'Failed: BOM validation for variant #001R-0031 failed',
    dateLabel: 'Today',
    canRetry: true,
    progress: null
  },
  {
    id: 'bom-validation-in-progress-1',
    feature: ACTIVITY_CENTER_FEATURE_TAB_NAMES.BOM_VALIDATIONS,
    status: ACTIVITY_CENTER_TAB_NAMES.IN_PROGRESS,
    title: 'Feature name',
    createdAt: new Date(Date.now() - 2 * 60000).toISOString(),
    message: 'In progress: BOM validation for variant #001R-0031 in progress',
    canRetry: false,
    progress: 5
  },
  {
    id: 'comb-cal-in-progress-2',
    feature: ACTIVITY_CENTER_FEATURE_TAB_NAMES.AMBIGUOUS_COMBINATIONS,
    status: ACTIVITY_CENTER_TAB_NAMES.IN_PROGRESS,
    title: 'Feature name',
    createdAt: new Date(Date.now() - 7 * 60 * 60000).toISOString(),
    message: 'In progress: Combination calculation in progress',
    canRetry: false,
    progress: 70
  },
  {
    id: 'test-case-validation-completed-1',
    feature: ACTIVITY_CENTER_FEATURE_TAB_NAMES.TEST_CASE_VALIDATIONS,
    status: ACTIVITY_CENTER_TAB_NAMES.COMPLETED,
    title: 'Feature name',
    createdAt: new Date().toISOString(),
    message: 'Completed: Test case validation 1 completed',
    progress: null
  },
  {
    id: 'test-case-validation-completed-2',
    feature: ACTIVITY_CENTER_FEATURE_TAB_NAMES.TEST_CASE_VALIDATIONS,
    status: ACTIVITY_CENTER_TAB_NAMES.COMPLETED,
    title: 'Feature name',
    createdAt: new Date(Date.now() - 24 * 60 * 60000).toISOString(),
    message: 'Completed: Test case validation 2 completed',
    progress: null
  },
  {
    id: 'test-case-validation-completed-3',
    feature: ACTIVITY_CENTER_FEATURE_TAB_NAMES.TEST_CASE_VALIDATIONS,
    status: ACTIVITY_CENTER_TAB_NAMES.COMPLETED,
    title: 'Feature name',
    createdAt: new Date(Date.now() - 4 * 24 * 60 * 60000).toISOString(),
    message: 'Completed: Test case validation 3 completed',
    progress: null
  },
  {
    id: 'test-case-validation-completed-4',
    feature: ACTIVITY_CENTER_FEATURE_TAB_NAMES.TEST_CASE_VALIDATIONS,
    status: ACTIVITY_CENTER_TAB_NAMES.COMPLETED,
    title: 'Feature name',
    createdAt: new Date(Date.now() - 18 * 24 * 60 * 60000).toISOString(),
    message: 'Completed: Test case validation 4 completed',
    progress: null
  }
])

export const filterActivitiesByStatus = (activities, status) => {
  if (status === ACTIVITY_CENTER_TAB_NAMES.ALL) return activities

  return activities.filter(activity => activity.status === status)
}

export const filterActivitiesByFeature = (activities, feature) => {
  if (feature === ACTIVITY_CENTER_FEATURE_TAB_NAMES.ALL) return activities

  return activities.filter(activity => activity.feature === feature)
}

export const getActivityCenterStatusTabs = activities => ACTIVITY_CENTER_TABS.map(tab => ({
  ...tab,
  count: filterActivitiesByStatus(activities, tab.name).length
}))

export const getActivityCenterFeatureTabs = ({ activities, status }) => {
  const activitiesByStatus = filterActivitiesByStatus(activities, status)

  return ACTIVITY_CENTER_FEATURE_TABS.map(tab => ({
    ...tab,
    count: filterActivitiesByFeature(activitiesByStatus, tab.name).length
  }))
}

const getStartOfDay = date => new Date(
  date.getFullYear(),
  date.getMonth(),
  date.getDate()
)

export const getActivityDate = activity => {
  if (!activity.createdAt) return null

  const date = new Date(activity.createdAt)

  return Number.isNaN(date.getTime()) ? null : date
}

const getActivityTimePeriodName = activity => {
  const activityDate = getActivityDate(activity)

  if (!activityDate) return null

  const today = getStartOfDay(new Date())
  const activityDay = getStartOfDay(activityDate)
  const differenceInDays = Math.floor((today - activityDay) / 86400000)

  if (differenceInDays === 0) return ACTIVITY_CENTER_TIME_PERIOD_NAMES.TODAY
  if (differenceInDays === 1) return ACTIVITY_CENTER_TIME_PERIOD_NAMES.YESTERDAY
  if (differenceInDays <= 7) return ACTIVITY_CENTER_TIME_PERIOD_NAMES.LAST_7_DAYS
  if (differenceInDays <= 30) return ACTIVITY_CENTER_TIME_PERIOD_NAMES.LAST_30_DAYS

  return null
}

export const isActivityWithinVisibleTimePeriod = activity => (
  getActivityTimePeriodName(activity) !== null
)

export const getActivityCenterRelativeTime = createdAt => {
  if (!createdAt) return ''

  const date = new Date(createdAt)

  if (Number.isNaN(date.getTime())) return createdAt

  const now = new Date()
  const differenceInMilliseconds = now - date

  if (differenceInMilliseconds < 0) return 'just now'

  const differenceInMinutes = Math.floor(differenceInMilliseconds / 60000)

  if (differenceInMinutes < 1) return 'just now'
  if (differenceInMinutes === 1) return '1 min ago'
  if (differenceInMinutes < 60) return `${ differenceInMinutes } min ago`

  const differenceInHours = Math.floor(differenceInMinutes / 60)

  if (differenceInHours === 1) return '1h ago'
  if (differenceInHours < 24) return `${ differenceInHours }h ago`

  const differenceInDays = Math.floor(differenceInHours / 24)

  if (differenceInDays === 1) return '1 day ago'
  if (differenceInDays <= 30) return `${ differenceInDays } days ago`

  return date.toLocaleDateString('en-GB')
}

export const getActivityCenterTimePeriodGroups = activities => ACTIVITY_CENTER_TIME_PERIODS
  .map(timePeriod => ({
    ...timePeriod,
    activities: activities.filter(activity => getActivityTimePeriodName(activity) === timePeriod.name)
  }))
  .filter(timePeriod => timePeriod.activities.length)
